import React, {
  useCallback,
  useMemo,
} from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Cancel from '@mui/icons-material/Cancel';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Typography from '@mui/material/Typography';

import {
  BooleanInput,
  DatagridConfigurable,
  HttpError,
  RecordContextProvider,
  SelectColumnsButton,
  useDataProvider,
  useNotify,
  usePermissions,
  useRecordContext,
  useStore,
} from 'react-admin';

import { useFormContext, useWatch } from 'react-hook-form';

import { DateTime } from 'luxon';
import { get, isNil, isUndefined } from 'lodash';
import { useQueries } from '@tanstack/react-query';

import TransactionTypeConfigurationField from '../../customFields/TransactionTypeConfigurationField';
import DatapointStatusField from './DatapointStatusField';
import CompareTransactionBehaviorField from './CompareTransactionBehaviorField';
import AddTransactionTypeDialogButton, { TransactionTypeConfiguration } from './AddTransactionTypeDialogButton';
import { EntityTypes } from '../../constants/entityTypes';
import { requestTypes } from '../../constants/requestTypes';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import Params from './Params';
import useDialogStatus from '../../hooks/useDialogStatus';
import transactionTableColumnSources from '../../constants/transactionTableColumnSources';
import { DemoData } from '../transactionTableSetup/TransactionTableSetup';
import RequestGraphsStatsResponse from '../../constants/RequestGraphsStatsResponse';
import ExpectedBehaviourFilter from './ExpectedBehaviourFilter';
import TransactionTableSetup from '../../constants/transactionTableSetup';
import useUpdatedTableFromConfig from '../../hooks/useUpdatedTableFromConfig';
import { ExpectedBehaviour } from '../../utilities/schemas/Snapshot';
import hasAccess from '../../utilities/hasAccess';
import actions, { Actions } from '../../constants/actions';

const rowSx = () => ({
  whiteSpace: 'nowrap',
});

const DeleteRowButton = ({
  removeRow,
  disabled,
}: {
  removeRow: (
    typeConfiguration: TransactionTypeConfiguration,
    index: number,
    id?: string,
  ) => void;
  source: string;
  label: false;
  disabled?: boolean;
}) => {
  const record = useRecordContext<{
    id: string;
    index: number;
    typeConfiguration?: TransactionTypeConfiguration;
  }>();

  const { open, openDialog, closeDialog } = useDialogStatus();

  const onClick = useCallback(() => {
    if (!record?.typeConfiguration) return;
    removeRow(record.typeConfiguration, record.index, record.id);
    closeDialog();
  }, [closeDialog, record?.id, record?.index, record?.typeConfiguration, removeRow]);

  if (!record?.typeConfiguration) return null;

  return (
    <>
      <IconButton onClick={openDialog} size="small" disabled={disabled}>
        <RemoveCircleOutlineIcon color="background" />
      </IconButton>
      <Dialog scroll="body" open={open} onClose={closeDialog}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Delete row
            <IconButton onClick={closeDialog}>
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the row?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} variant="text">Cancel</Button>
          <Button onClick={onClick} variant="text">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SelectRowRadioButton = ({
  source,
  onChangeSelectedRow,
  disabled,
}: {
  source: string;
  label: string;
  onChangeSelectedRow?: (newValue: string | undefined) => void;
  disabled?: boolean;
}) => {
  const record = useRecordContext();
  const value = get(record, transactionTableColumnSources.TYPE_CONFIGURATION);

  const { setValue } = useFormContext();
  const formValue = useWatch({ name: source });

  const stringifiedValue = isNil(value) ? undefined : JSON.stringify(value);

  const onChange = useCallback(() => {
    setValue(source, stringifiedValue);
    if (onChangeSelectedRow) {
      onChangeSelectedRow(stringifiedValue);
    }
  }, [setValue, stringifiedValue, source, onChangeSelectedRow]);

  return (
    <Radio
      checked={stringifiedValue === formValue}
      value={stringifiedValue}
      onChange={onChange}
      disabled={disabled}
    />
  );
};

export const useGetConfigurationSummaries = (
  typeConfigurations: (TransactionTypeConfiguration | undefined)[],
  filter: {
    from: string;
    to: string;
    currency: string;
    entityId: string;
    entityType: EntityTypes;
    requestType: typeof requestTypes.TRANSACTION;
  },
  isDemo: boolean,
) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queries = useQueries({
    queries: typeConfigurations.map((configuration) => {
      const queryParams = new URLSearchParams();
      queryParams.append('from', filter.from);
      queryParams.append('to', filter.to);
      queryParams.append('currency', filter.currency);
      queryParams.append('entityId', filter.entityId);
      queryParams.append('entityType', filter.entityType);
      queryParams.append('requestType', filter.requestType);
      if (configuration) queryParams.append('transactionTypeConfiguration', JSON.stringify(configuration));

      const id = `?${queryParams.toString()}`;

      return {
        queryKey: ['requests/graph/stats', 'getOne', { id }],
        queryFn: async () => {
          const response = await dataProvider.getOne<RequestGraphsStatsResponse>('requests/graph/stats', { id });
          return {
            ...response.data,
            id,
          };
        },
        enabled: !!filter.from && !!filter.to && !!filter.currency && !isDemo,
        onError: (e: unknown) => {
          notify(e instanceof HttpError && e.status === 400 ? e.message : 'Cannot fetch transaction summary', { type: 'error' });
        },
      };
    }),
  });

  return queries;
};

const TransactionSummaryTable = ({
  params,
  filter,
  defaultConfiguration,
  preferenceKey,
  showExpectedBehaviourInput,
  showExpectedBehaviour,
  expectedBehaviourCache,
  shouldDisableInput,
  onChangeSelectedRow,
  demo,
}: {
  params: Params;
  filter: {
    from: string;
    to: string;
    currency: string;
    entityId: string;
    entityType: EntityTypes;
    requestType: typeof requestTypes.TRANSACTION;
  };
  defaultConfiguration?: TransactionTableSetup;
  preferenceKey: string;
  showExpectedBehaviourInput?: boolean;
  showExpectedBehaviour?: boolean;
  expectedBehaviourCache?: ExpectedBehaviour[];
  shouldDisableInput?: boolean;
  onChangeSelectedRow?: (newValue: string | undefined) => void;
  demo?: {
    demoData: DemoData[];
    addRow: (transactionTypeConfiguration: TransactionTypeConfiguration) => void;
    deleteRow: (rowId: string) => void;
  };
}) => {
  const {
    typeConfigurations,
    setTypeConfigurations,
  } = useUpdatedTableFromConfig({ preferenceKey, defaultConfiguration });
  const { permissions } = usePermissions<Actions[]>();

  const results = useGetConfigurationSummaries(typeConfigurations, filter, !!demo);
  const isLoading = results.some((result) => result.isLoading);

  const record = useMemo(() => (demo?.demoData ?? results).map((result, index) => ({
    ...result.data,
    id: demo?.demoData ? result.data?.id : `${index}`,
    index,
    netVolume: result.data ? result.data.incoming.volume - result.data.outgoing.volume : undefined,
    typeConfiguration: typeConfigurations[index],
    expectedBehaviour: expectedBehaviourCache
      ? expectedBehaviourCache
        .filter((behaviour) => behaviour.transactionTypeConfiguration === typeConfigurations[index])
        .map((behaviour) => ({
          ...behaviour,
          value: {
            ...behaviour.value,
            currency: filter.currency,
          },
        }))
      : undefined,
  })), [
    demo?.demoData,
    expectedBehaviourCache,
    filter.currency,
    results,
    typeConfigurations,
  ]);

  const diff = DateTime.fromISO(filter.to).diff(DateTime.fromISO(filter.from), 'months');
  const transformMonthlyStat = useCallback((value: number) => value * diff.months, [diff.months]);

  const addRow = useCallback((typeConfiguration?: TransactionTypeConfiguration) => {
    setTypeConfigurations((currentValue) => [...currentValue, typeConfiguration]);
    if (demo && !isUndefined(typeConfiguration)) {
      demo.addRow(typeConfiguration);
    }
  }, [demo, setTypeConfigurations]);

  const removeRow = useCallback((
    typeConfiguration: TransactionTypeConfiguration,
    index: number,
    id?: string,
  ) => {
    if (demo && id) {
      demo.deleteRow(id);
    }
    const stringifiedSelectedConfig = JSON.stringify(typeConfiguration);
    setTypeConfigurations((currentValue) => currentValue.filter((configuration, currentIndex) => !(
      configuration
      && JSON.stringify(configuration) === stringifiedSelectedConfig
      && currentIndex === index
    )));
  }, [demo, setTypeConfigurations]);

  const [
    showExpectedBehavior,
    setShowExpectedBehavior,
  ] = useStore('transactionShowExpectedBehavior', showExpectedBehaviour ?? true);

  const onChangeShowExpected = useCallback(() => {
    setShowExpectedBehavior((currentValue) => !currentValue);
  }, [setShowExpectedBehavior]);

  return (
    <>
      <Box display="flex" width="100%" justifyContent="flex-end" alignItems="center" gap={2}>
        {showExpectedBehavior && showExpectedBehaviourInput && !shouldDisableInput && (
          <ExpectedBehaviourFilter
            entityId={filter.entityId}
            entityType={filter.entityType}
          />
        )}
        {showExpectedBehaviourInput && (
          <RecordContextProvider value={{ showExpectedBehavior }}>
            <BooleanInput
              source="showExpectedBehavior"
              onChange={onChangeShowExpected}
              helperText={false}
              disabled={shouldDisableInput}
            />
          </RecordContextProvider>
        )}
        {(!demo || hasAccess(permissions, actions.TRANSACTION_TABLE_SETUP_UPDATE_COLUMNS)) && (
          <SelectColumnsButton preferenceKey={preferenceKey} />
        )}
      </Box>
      <Card variant="outlined" sx={{ marginY: 1 }}>
        <DatagridConfigurable
          resource="data-points"
          data={record}
          total={record.length}
          isLoading={isLoading}
          bulkActionButtons={false}
          sort={{ field: '_id', order: 'DESC' }}
          rowSx={rowSx}
          sx={boldDataGridStyle}
          preferenceKey={preferenceKey}
        >
          <SelectRowRadioButton
            source={transactionTableColumnSources.SELECTED_ROW}
            label="Show in graph"
            onChangeSelectedRow={onChangeSelectedRow}
            disabled={shouldDisableInput}
          />
          <TransactionTypeConfigurationField
            source={transactionTableColumnSources.TYPE_CONFIGURATION}
            label="Transaction type"
          />
          {showExpectedBehavior && (
            <DatapointStatusField
              source={transactionTableColumnSources.CONFLICT_STATUS}
              label="Conflict status"
              entityId={filter.entityId}
              entityType={filter.entityType}
              isDemo={!!demo}
            />
          )}
          <CompareTransactionBehaviorField
            source={transactionTableColumnSources.INCOMING_VOLUME}
            options={{ style: 'currency', currency: params.currency, currencyDisplay: 'code' }}
            comparedSource={transactionTableColumnSources.INCOMING_MONTHLY_VOLUME}
            comparedTransform={transformMonthlyStat}
            showExpectedBehavior={showExpectedBehavior && (!demo || !!expectedBehaviourCache)}
            entityId={filter.entityId}
            entityType={filter.entityType}
            currency={params.currency}
            textAlign="right"
          />
          <CompareTransactionBehaviorField
            source={transactionTableColumnSources.OUTGOING_VOLUME}
            options={{ style: 'currency', currency: params.currency, currencyDisplay: 'code' }}
            comparedSource={transactionTableColumnSources.OUTGOING_MONTHLY_VOLUME}
            comparedTransform={transformMonthlyStat}
            showExpectedBehavior={showExpectedBehavior && (!demo || !!expectedBehaviourCache)}
            entityId={filter.entityId}
            entityType={filter.entityType}
            currency={params.currency}
            textAlign="right"
          />
          <CompareTransactionBehaviorField
            source={transactionTableColumnSources.PROCESSING_VOLUME}
            options={{ style: 'currency', currency: params.currency, currencyDisplay: 'code' }}
            comparedSource={transactionTableColumnSources.PROCESSING_MONTHLY_VOLUME}
            comparedTransform={transformMonthlyStat}
            showExpectedBehavior={showExpectedBehavior && (!demo || !!expectedBehaviourCache)}
            entityId={filter.entityId}
            entityType={filter.entityType}
            currency={params.currency}
            textAlign="right"
          />
          <CompareTransactionBehaviorField
            source={transactionTableColumnSources.TOTAL_VOLUME}
            options={{ style: 'currency', currency: params.currency, currencyDisplay: 'code' }}
            comparedSource={transactionTableColumnSources.TOTAL_MONTHLY_VOLUME}
            comparedTransform={transformMonthlyStat}
            showExpectedBehavior={showExpectedBehavior && (!demo || !!expectedBehaviourCache)}
            entityId={filter.entityId}
            entityType={filter.entityType}
            currency={params.currency}
            textAlign="right"
          />
          <CompareTransactionBehaviorField
            source={transactionTableColumnSources.NET_VOLUME}
            options={{ style: 'currency', currency: params.currency, currencyDisplay: 'code' }}
            comparedSource={transactionTableColumnSources.NET_MONTHLY_VOLUME}
            comparedTransform={transformMonthlyStat}
            showExpectedBehavior={showExpectedBehavior && (!demo || !!expectedBehaviourCache)}
            entityId={filter.entityId}
            entityType={filter.entityType}
            currency={params.currency}
            textAlign="right"
          />
          <CompareTransactionBehaviorField
            source={transactionTableColumnSources.INCOMING_FREQUENCY}
            label="Number incoming transactions"
            comparedSource={transactionTableColumnSources.INCOMING_MONTHLY_FREQUENCY}
            comparedTransform={transformMonthlyStat}
            showExpectedBehavior={showExpectedBehavior && (!demo || !!expectedBehaviourCache)}
            entityId={filter.entityId}
            entityType={filter.entityType}
            currency={params.currency}
            textAlign="right"
          />
          <CompareTransactionBehaviorField
            source={transactionTableColumnSources.OUTGOING_FREQUENCY}
            label="Number outgoing transactions"
            comparedSource={transactionTableColumnSources.OUTGOING_MONTHLY_FREQUENCY}
            comparedTransform={transformMonthlyStat}
            showExpectedBehavior={showExpectedBehavior && (!demo || !!expectedBehaviourCache)}
            entityId={filter.entityId}
            entityType={filter.entityType}
            currency={params.currency}
            textAlign="right"
          />
          <CompareTransactionBehaviorField
            source={transactionTableColumnSources.PROCESSING_FREQUENCY}
            label="Number processing transactions"
            comparedSource={transactionTableColumnSources.PROCESSING_MONTHLY_FREQUENCY}
            comparedTransform={transformMonthlyStat}
            showExpectedBehavior={showExpectedBehavior && (!demo || !!expectedBehaviourCache)}
            entityId={filter.entityId}
            entityType={filter.entityType}
            currency={params.currency}
            textAlign="right"
          />
          <CompareTransactionBehaviorField
            source={transactionTableColumnSources.TOTAL_FREQUENCY}
            label="Total number transactions"
            comparedSource={transactionTableColumnSources.TOTAL_MONTHLY_FREQUENCY}
            comparedTransform={transformMonthlyStat}
            showExpectedBehavior={showExpectedBehavior && (!demo || !!expectedBehaviourCache)}
            entityId={filter.entityId}
            entityType={filter.entityType}
            currency={params.currency}
            textAlign="right"
          />
          <DeleteRowButton
            source={transactionTableColumnSources.REMOVE_ROW}
            removeRow={removeRow}
            disabled={shouldDisableInput}
            label={false}
          />
        </DatagridConfigurable>
      </Card>
      {!shouldDisableInput
      && (!demo?.addRow || hasAccess(permissions, actions.TRANSACTION_TABLE_SETUP_UPDATE_ROW)) && (
        <Box display="flex" width="100%" justifyContent="flex-start">
          <AddTransactionTypeDialogButton onConfirm={addRow} />
        </Box>
      )}
    </>
  );
};

export default TransactionSummaryTable;
